import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { useQuery } from '@tanstack/react-query';

import type { PaginationType, Params } from '@codes/shared';
import { createQueryKeys, useCustomMutation } from '@codes/shared';

import {
    clonePrompt,
    createAgent,
    createPrompt,
    deleteAgent,
    deletePrompt,
    fetchAgentById,
    fetchAgents,
    fetchDefaultPrompts,
    fetchPromptById,
    fetchPrompts,
    fetchRevisionList,
    generateAIResponse,
    revisePrompt,
    updateAgent,
} from '../api';

import type {
    ClonePromptDTO,
    CreateAgentDTO,
    CreatePromptDTO,
    GenerateAIResponseDTO,
    UpdateAgentDTO,
    UpdatePromptDTO,
} from '../types';
import type { AgentView, PromptView } from '@lemoncloud/ssocio-chatbots-api';

export const promptKeys = createQueryKeys('prompts');
export const agentsKeys = createQueryKeys('agents');
export const revisionKeys = createQueryKeys('revision');

export const useAgents = (params: Params) =>
    useQuery<PaginationType<AgentView[]>>({
        queryKey: agentsKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchAgents(params);
            return { ...result, data: result.list.filter(data => !data.deletedAt) || [] } as PaginationType<
                AgentView[]
            >;
        },
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        staleTime: 0,
    });

export const useAgent = (agentId: string, enabled: boolean) =>
    useQuery<AgentView>({
        queryKey: agentsKeys.detail(agentId),
        queryFn: async () => {
            const result = await fetchAgentById(agentId);
            return result;
        },
        refetchOnWindowFocus: false,
        enabled,
    });

export const useCreateAgent = () => {
    return useCustomMutation((data: CreateAgentDTO) => createAgent(data), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useDeleteAgent = () => {
    return useCustomMutation((id: string) => deleteAgent(id), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useUpdateAgent = () => {
    return useCustomMutation((dto: UpdateAgentDTO) => updateAgent(dto), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const usePrompts = (params: Params) =>
    useQuery<PaginationType<PromptView[]>>({
        queryKey: promptKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchPrompts(params);
            return { ...result, data: result.list.filter(data => !data.deletedAt) || [] } as PaginationType<
                PromptView[]
            >;
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
        refetchOnMount: true,
    });

export const useTemplatePrompts = (params: Params) =>
    useQuery<PaginationType<PromptView[]>>({
        queryKey: promptKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchDefaultPrompts(params);
            return { ...result, data: result.list || [] } as PaginationType<PromptView[]>;
        },
        refetchOnWindowFocus: false,
        enabled: !!params.type,
    });

export const usePrompt = (promptId: string) =>
    useQuery<PromptView>({
        queryKey: promptKeys.detail(promptId),
        queryFn: async () => {
            return await fetchPromptById(promptId);
        },
        refetchOnWindowFocus: false,
        enabled: !!promptId,
    });

export const useCreatePrompt = () => {
    return useCustomMutation((data: CreatePromptDTO) => createPrompt(data), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useDeletePrompt = () => {
    return useCustomMutation((id: string) => deletePrompt(id), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useRevisePrompt = () => {
    return useCustomMutation((dto: UpdatePromptDTO) => revisePrompt(dto), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const useGenerateAIResponse = () => {
    return useCustomMutation((dto: GenerateAIResponseDTO) => generateAIResponse(dto), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};

export const usePromptRevisions = (params: Params) =>
    useQuery<PaginationType<PromptView[]>>({
        queryKey: revisionKeys.list(params ?? {}),
        queryFn: async () => {
            const result = await fetchRevisionList(params);
            return { ...result, data: result.list || [] } as PaginationType<PromptView[]>;
        },
        refetchOnWindowFocus: false,
        enabled: !!params['promptId'],
    });

export const useClonePrompt = () => {
    return useCustomMutation((data: ClonePromptDTO) => clonePrompt(data), {
        onError: error => {
            toast({ title: error instanceof Error ? error.message : 'An unknown error occurred' });
        },
    });
};
